import { faComment } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import React from "react";

import SimpleSelect from "@/mk2/apps/bazaar/components/SimpleSelect";
import { renderCover } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import { MorePosts } from "@/mk2/apps/forum/components/MorePosts";
import { WikiArticles } from "@/mk2/apps/home/components/WikiArticles";
import { strollersPrefix } from "@/mk2/apps/strollers/urls";
import AIReviewSummaryCard from "@/mk2/apps/wiki/components/AIReviewSummaryCard";
import { ArticleBodyContent, ArticleBodyHeader } from "@/mk2/apps/wiki/components/ArticleBody";
import ArticleBodyStyles from "@/mk2/apps/wiki/components/ArticleBody.mscss";
import Experience from "@/mk2/apps/wiki/components/Experience";
import QuestionsAndAnswersSection from "@/mk2/apps/wiki/components/QuestionsAndAnswersSection";
import ReviewArticleTabs, {
    Section,
    normalizeSectionForInternal,
    parseSection,
} from "@/mk2/apps/wiki/components/ReviewArticleTabs";
import { LayoutType } from "@/mk2/apps/wiki/components/ReviewArticles";
import ReviewCategoryPreview from "@/mk2/apps/wiki/components/ReviewCategoryPreview";
import { ReviewWhereToBuyList } from "@/mk2/apps/wiki/components/ReviewWhereToBuyList";
import { ReviewerAttribution } from "@/mk2/apps/wiki/components/ReviewerAttribution";
import Article from "@/mk2/apps/wiki/containers/Article/Article";
import { Props } from "@/mk2/apps/wiki/containers/Article/ReviewArticlePage";
import ReviewHomepageRecentBlogArticles from "@/mk2/apps/wiki/containers/ReviewHomepageRecentBlogArticles/ReviewHomepageRecentBlogArticles";
import { isArticleOfReviewsCategory, isReviewHomepageArticle } from "@/mk2/apps/wiki/helpers";
import {
    getWikiArticleUrl,
    wikiAddExperienceToUndefinedArticleUrl,
    wikiAddExperienceUrl,
    wikiArticleExperiencePhotoUrl,
    wikiArticlePhotoUrl,
} from "@/mk2/apps/wiki/urls";
import AnnouncementBox, { BoxType } from "@/mk2/components/AnnouncementBox";
import { BreadcrumbSiteStructure } from "@/mk2/components/BreadcrumbSiteStructure";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { PhotosRowOfSquares } from "@/mk2/components/PhotosRowOfSquares";
import { StarsRating } from "@/mk2/components/StarsRating";
import { TrackClick } from "@/mk2/components/TrackClick";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import Loadable from "@/mk2/helpers/Loadable";
import { cacheLast } from "@/mk2/helpers/cache";
import { hasVal } from "@/mk2/helpers/sanitize";
import { scrollElementTo } from "@/mk2/helpers/scrolling";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { ExperienceEntity, PhotoEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    RELATED_DISCUSSIONS_IN_FORUM,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_BEST,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_LIKES,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_NEWEST,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_PHOTOS,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_WORST,
    WIKI_DO_YOU_HAVE_EXPERIENCE_WITH_ANY_REVIEWABLE,
    WIKI_DO_YOU_HAVE_EXPERIENCE_WITH_title,
    WIKI_EXPERIENCES_WITH_REVIEWS__HP,
    WIKI_EXPERIENCES_WITH_title,
    WIKI_EXPERIENCE_SENT_TOAST,
    WIKI_EXP_AVERAGE_SCORE,
    WIKI_REVIEW_CATEGORY__NEW_ARTICLES_OF_category,
    WIKI_SHARE_YOUR_EXPERIENCE_AND_HELP_OTHERS,
    WIKI_WHERE_NEXT,
    WIKI_WRITE_YOUR_EXPERIENCE,
} from "@/mk/autogenerated/translations/ReviewArticle.1343f76c33c40785aee98ecb34113427";

import styles from "./Article.mscss";

const ReviewArticles = Loadable({
    loader: () =>
        import(
            "@/mk2/apps/wiki/components/ReviewArticles" /* webpackChunkName: "wiki.ReviewArticles" */
        ),
    modules: ["@/mk2/apps/wiki/components/ReviewArticles"],
    webpack: () => [require.resolveWeak("@/mk2/apps/wiki/components/ReviewArticles")],
});

interface OwnState {
    sortExperiencesBy: SortExperiencesBy;
    collapsedBreadcrumbs: boolean;
    articleSlug: string;
    activeSection: Section;
}

enum SortExperiencesBy {
    NEWEST = "newest",
    BEST = "best",
    WORST = "worst",
    PHOTOS = "photos",
    LIKES = "likes",
}

const sortOptions: Record<SortExperiencesBy, string> = {
    [SortExperiencesBy.NEWEST]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_NEWEST,
    [SortExperiencesBy.BEST]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_BEST,
    [SortExperiencesBy.WORST]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_WORST,
    [SortExperiencesBy.LIKES]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_LIKES,
    [SortExperiencesBy.PHOTOS]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_PHOTOS,
};

const sortingFns: Record<SortExperiencesBy, (a: ExperienceEntity, b: ExperienceEntity) => number> =
    {
        [SortExperiencesBy.NEWEST]: (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        [SortExperiencesBy.BEST]: (a, b) =>
            b.starsCount - a.starsCount ||
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        [SortExperiencesBy.WORST]: (a, b) =>
            a.starsCount - b.starsCount ||
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        [SortExperiencesBy.PHOTOS]: (a, b) =>
            b.photos.length - a.photos.length ||
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        [SortExperiencesBy.LIKES]: (a, b) =>
            b.likeable.likesCount - a.likeable.likesCount ||
            new Date(b.created).getTime() - new Date(a.created).getTime(),
    };

const MAX_WIDTH = 584;

class ReviewArticle extends React.Component<Props, OwnState> {
    public static getDerivedStateFromProps(props: Props, state: OwnState) {
        if (props.articleSlug !== state.articleSlug) {
            return {
                articleSlug: props.articleSlug,
                collapsedBreadcrumbs: true,
            };
        }

        return null;
    }

    public state: OwnState = {
        collapsedBreadcrumbs: true,
        articleSlug: this.props.articleSlug,
        sortExperiencesBy: SortExperiencesBy.NEWEST,
        activeSection: normalizeSectionForInternal(parseSection(this.props.location.search)),
    };

    private sortedExperiences = cacheLast<ExperienceEntity[]>(true);

    private galleryPhotosData = cacheLast<{
        photos: PhotoEntity[] | null;
        galleryLinkTo: (photofile: PhotoEntity) => string | null;
    }>(true);

    public componentDidMount() {
        const { articleSlug, categorySlug, experienceIdRef, loadingState, onPageView } = this.props;
        onPageView(categorySlug, articleSlug);

        // Scroll only if content is loaded
        if (experienceIdRef && isSuccess(loadingState)) {
            try {
                scrollElementTo(experienceIdRef, "window-top", 50);
            } catch (error) {
                // ignore, no need to show error page because of this
            }
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { articleSlug, categorySlug, experienceIdRef, loadingState, location, onPageView } =
            this.props;

        if (prevProps.routeParams.articleSlug !== articleSlug) {
            onPageView(categorySlug, articleSlug);
        }

        if (
            experienceIdRef &&
            prevProps.loadingState === LoadingState.LOADING &&
            loadingState === LoadingState.SUCCESS
        ) {
            // Content is now loaded
            scrollElementTo(experienceIdRef, "window-top");
        }

        // Handle section change
        if (prevProps.location.search !== location.search) {
            const newSection = normalizeSectionForInternal(parseSection(location.search));
            if (newSection !== this.state.activeSection) {
                this.setState({ activeSection: newSection });
                // Scroll to top of tabs
                if (document.getElementById("review-article-tabs")) {
                    scrollElementTo("#review-article-tabs", "window-top");
                }
            }
        }
    }

    public render() {
        const { loadingState, article } = this.props;
        return (
            <LoadingSwitch
                loadingState={loadingState}
                onLoad={this.load}
                onRenderInit={this.renderInit}
                onRenderSuccess={this.renderLoadSuccess}
                onRenderFailure={this.renderFailure}
                hasData={!!article}
            />
        );
    }

    private load = () => {
        const { articleSlug, categorySlug, onLoad } = this.props;
        onLoad(articleSlug, categorySlug);
    };

    private renderInit = () => {
        const { articleSlug, isMobile } = this.props;
        return (
            <>
                {Article.renderInit(isMobile)}
                <ReviewHomepageRecentBlogArticles
                    articleSlug={articleSlug}
                    key={"recent-blog-articles"}
                />
            </>
        );
    };

    private renderFailure = () => {
        const { errorMessage } = this.props;
        return Article.renderFailure(errorMessage, this.load);
    };

    private renderLoadSuccess = () => {
        const {
            article,
            articleSlug,
            articleConceptPath,
            experienceSubmitted,
            isMobile,
            linkedThreads,
            location,
            onDeleteVendorReply,
            onSaveVendorReply,
            onToggleMenuMore,
            relatedArticles,
            requestUser,
            requestUserPermissions,
            reviewCategoryArticles,
            reviewCategoryArticlesCount,
            reviewCategoryExperiences,
            reviewCategories,
            reviewCategoriesStats,
            viewsCountUnique,
            viewsCountTotal,
            moreReviewCategoryArticlesLoadingState,
        } = this.props;

        const cover = renderCover(article);

        const isAdmin = requestUserPermissions.indexOf("wiki.can_edit_wiki") >= 0;

        /**
         * - Review homepage article
         *   - Review category article
         *     - Review category article
         *       - Review product article
         *       - Review product article
         *     - Review product article
         *     - Review product article
         *   - Review category article
         *     - Review product article
         */
        const isReviewCategory = hasVal(reviewCategoryArticlesCount);
        const isReviewHomepageOrCategoryOrProduct = isArticleOfReviewsCategory(
            article.category.slug,
        );
        const isReviewProduct = isReviewHomepageOrCategoryOrProduct && !isReviewCategory;

        const showAlsoCurrentArticleInBreadcrumbs = isReviewCategory;

        const experiences = this.sortedExperiences(
            () => {
                const exps = isReviewCategory ? reviewCategoryExperiences : article.experiences;
                return exps.sort(sortingFns[this.state.sortExperiencesBy]);
            },
            isReviewCategory,
            reviewCategoryExperiences,
            article.experiences,
            this.state.sortExperiencesBy,
        );

        const subCategoriesOfCurrentCategory =
            isReviewCategory && reviewCategories ? (
                <>
                    {reviewCategories.map((reviewCategory) => (
                        <ReviewCategoryPreview
                            key={reviewCategory.id}
                            name={reviewCategory.article.title}
                            faIcon={reviewCategory.icon}
                            url={getWikiArticleUrl(
                                reviewCategory.article.category.slug,
                                reviewCategory.article.slug,
                            )}
                            reviewArticleCount={
                                reviewCategoriesStats?.[reviewCategory.article.id]?.productsCount
                            }
                            reviewExperiencesCount={
                                reviewCategoriesStats?.[reviewCategory.article.id]?.experiencesCount
                            }
                        />
                    ))}
                </>
            ) : null;
        const reviewArticlesOfCurrentCategory = isReviewCategory ? (
            <ReviewArticles
                header={interpolate(WIKI_REVIEW_CATEGORY__NEW_ARTICLES_OF_category, {
                    category: article.title,
                })}
                articles={reviewCategoryArticles}
                count={reviewCategoryArticlesCount}
                onLoadMore={
                    reviewCategoryArticles.length === reviewCategoryArticlesCount
                        ? null
                        : this.handleOnLoadMoreReviewCategoryArticles
                }
                layout={LayoutType.DEFAULT}
                loadingState={moreReviewCategoryArticlesLoadingState}
            />
        ) : null;

        // Merge photos attached to reviews into the top gallery so reviewers'
        // real photos surface alongside the product gallery (AI/GEO priority).
        // Track which review each merged photo belongs to so its lightbox link
        // points at the review, not the article photo route. Memoized so the
        // O(reviews*photos) merge only reruns when the inputs actually change.
        // linkTo is built inside the memo too, so its identity is stable across
        // re-renders (PhotosRowOfSquares is React.memo — a fresh function each
        // render would defeat it).
        const { photos: photosInPhotosRow, galleryLinkTo } = this.galleryPhotosData(
            () => {
                const base = isReviewProduct
                    ? cover
                        ? article.photos.filter((photo) => photo.code !== cover.photoCode)
                        : article.photos
                    : null;
                const map = new Map<number, number>();
                const photos = base ? [...base] : null;
                if (isReviewProduct && photos) {
                    const seenPhotoIds = new Set(photos.map((photo) => photo.id));
                    for (const experience of article.experiences ?? []) {
                        if (!experience.approved || experience.deleted) {
                            continue;
                        }
                        for (const experiencePhoto of experience.photos ?? []) {
                            const photofile = experiencePhoto.photofile;
                            if (seenPhotoIds.has(photofile.id)) {
                                continue;
                            }
                            seenPhotoIds.add(photofile.id);
                            photos.push(photofile);
                            map.set(photofile.id, experience.id);
                        }
                    }
                }
                const linkTo = (photofile: PhotoEntity) => {
                    const experienceId = map.get(photofile.id);
                    if (experienceId !== undefined) {
                        return url(wikiArticleExperiencePhotoUrl, {
                            categorySlug: article.category.slug,
                            articleSlug: article.slug,
                            experienceId,
                            photoId: photofile.id,
                        });
                    }
                    return url(wikiArticlePhotoUrl, {
                        categorySlug: article.category.slug,
                        articleSlug: article.slug,
                        photoId: photofile.id,
                    });
                };
                return { photos, galleryLinkTo: linkTo };
            },
            isReviewProduct,
            cover ? cover.photoCode : null,
            article.photos,
            article.experiences,
            article.category.slug,
            article.slug,
        );

        const addExpUrl = !isReviewCategory
            ? url(wikiAddExperienceUrl, {
                  articleSlug: article.slug,
                  categorySlug: article.category.slug,
              })
            : url(wikiAddExperienceToUndefinedArticleUrl, {});

        const hasWhereToBuy = isReviewProduct && (article.whereToBuyItems?.length ?? 0) > 0;
        const hasDiscussions = isReviewProduct && linkedThreads && linkedThreads.length > 0;
        const hasReviews = isReviewProduct && experiences.length > 0;
        const hasQuestionsAndAnswers =
            isReviewProduct &&
            ((article.answeredQuestions?.length ?? 0) > 0 ||
                !!article.seoFaqRichSnippet ||
                (article.questionsAllowed ?? false));

        return (
            <>
                {experienceSubmitted && (
                    <AnnouncementBox
                        message={WIKI_EXPERIENCE_SENT_TOAST}
                        hasDetailMessage={false}
                        type={BoxType.Yellow}
                    />
                )}
                <div className={styles.Article}>
                    {article.category.slug !== strollersPrefix && (
                        <BreadcrumbSiteStructure
                            className={styles.Article__breadcrumb}
                            items={
                                articleConceptPath.length > 4 && this.state.collapsedBreadcrumbs
                                    ? Article.getCollapsedBreadcrumbLinks(
                                          articleConceptPath,
                                          this.onClickSetState,
                                          showAlsoCurrentArticleInBreadcrumbs,
                                      )
                                    : Article.getBreadcrumbLinks(
                                          articleConceptPath,
                                          showAlsoCurrentArticleInBreadcrumbs,
                                      )
                            }
                        />
                    )}

                    {isReviewProduct && article.aiReviewSummaryAST && (
                        <AIReviewSummaryCard article={article} />
                    )}

                    {cover && <div className={styles.Article__cover}>{cover.rendered}</div>}

                    {photosInPhotosRow && photosInPhotosRow.length > 0 && (
                        <PhotosRowOfSquares
                            className={cx(
                                styles.Article__photosRow,
                                photosInPhotosRow.length > 6
                                    ? styles["Article__photosRow--stretchedAndCentered"]
                                    : styles["Article__photosRow--alignLeft"],
                            )}
                            photoVersion="s246x246"
                            maxWidth={MAX_WIDTH}
                            photos={photosInPhotosRow}
                            photoAlt={article.title}
                            linkTo={galleryLinkTo}
                        />
                    )}

                    <div className={ArticleBodyStyles.ArticleBody}>
                        <ArticleBodyHeader
                            article={article}
                            location={location}
                            viewsCountUnique={viewsCountUnique}
                            viewsCountTotal={viewsCountTotal}
                            followLinks
                            isMobile={isMobile}
                            isReviewCategory={isReviewCategory}
                            requestUser={requestUser}
                            requestUserPermissions={requestUserPermissions}
                        />

                        {isReviewProduct && (
                            <div
                                id="review-article-tabs"
                                className={styles.Article__reviewArticleTabs}
                            >
                                <ReviewArticleTabs
                                    location={location}
                                    hasReviews={hasReviews}
                                    hasWhereToBuy={hasWhereToBuy}
                                    hasDiscussions={hasDiscussions}
                                    hasQuestionsAndAnswers={hasQuestionsAndAnswers}
                                />
                            </div>
                        )}

                        {this.shouldShowSection("default", this.state.activeSection) && (
                            <div className={styles.Article__bodyContent}>
                                <ArticleBodyContent
                                    article={article}
                                    location={location}
                                    viewsCountUnique={viewsCountUnique}
                                    viewsCountTotal={viewsCountTotal}
                                    followLinks
                                    isMobile={isMobile}
                                    isReviewCategory={isReviewCategory}
                                    requestUser={requestUser}
                                    requestUserPermissions={requestUserPermissions}
                                />
                                <ReviewerAttribution entityLinks={article.entityLinks} />
                            </div>
                        )}

                        {this.shouldShowSection("ponuka", this.state.activeSection) &&
                            hasWhereToBuy && (
                                <div className={styles.Article__whereToBuy}>
                                    <ReviewWhereToBuyList
                                        title={article.whereToBuyTitle}
                                        manualItems={article.whereToBuyItems}
                                        trackClickWithDetails={{
                                            article_id: article.id,
                                            category_slug: article.category.slug,
                                            article_slug: article.slug,
                                        }}
                                    />
                                </div>
                            )}

                        {this.shouldShowSection("diskusie", this.state.activeSection) &&
                            hasDiscussions && (
                                <MorePosts
                                    className={styles.Article__linkedThreads}
                                    posts={linkedThreads}
                                    title={RELATED_DISCUSSIONS_IN_FORUM}
                                    headingLevel={2}
                                    plain
                                />
                            )}

                        {this.shouldShowSection("otazky", this.state.activeSection) &&
                            (this.state.activeSection === "otazky" ||
                                article.answeredQuestions?.length > 0 ||
                                !!article.seoFaqRichSnippet ||
                                (article.questionsAllowed ?? false)) && (
                                <QuestionsAndAnswersSection
                                    seoFaqRichSnippet={article.seoFaqRichSnippet}
                                    answeredQuestions={article.answeredQuestions || []}
                                    questionsAllowed={article.questionsAllowed ?? false}
                                    isAdmin={isAdmin}
                                    categorySlug={article.category.slug}
                                    articleSlug={article.slug}
                                    requestUser={requestUser}
                                    showHeading={this.state.activeSection !== "otazky"}
                                />
                            )}
                    </div>

                    {this.shouldShowSection("hodnotenia", this.state.activeSection) &&
                        (article.experiencesAllowed || isReviewCategory) && (
                            <div
                                id={"experiences"}
                                className={cx(
                                    styles.ArticleExperiences,
                                    styles.Article__reviewExperiences,
                                )}
                            >
                                <h2 className={styles.ArticleExperiences__sectionHeader}>
                                    {isReviewHomepageArticle(article.category.slug, article.slug)
                                        ? WIKI_EXPERIENCES_WITH_REVIEWS__HP
                                        : interpolate(WIKI_EXPERIENCES_WITH_title, {
                                              title: article.instrumentalTitle,
                                          })}
                                </h2>

                                {article.experiencesStarsAllowed &&
                                    !isReviewHomepageOrCategoryOrProduct && (
                                        <StarsRating
                                            starsCount={article.experiencesScore}
                                            label={WIKI_EXP_AVERAGE_SCORE}
                                            className={styles.ArticleExperiences__starsRating}
                                        />
                                    )}

                                <div className={styles.ArticleExperiences__addExperience}>
                                    <div
                                        className={styles.ArticleExperiences__addExperience__header}
                                    >
                                        {isReviewHomepageArticle(
                                            article.category.slug,
                                            article.slug,
                                        )
                                            ? WIKI_DO_YOU_HAVE_EXPERIENCE_WITH_ANY_REVIEWABLE
                                            : interpolate(WIKI_DO_YOU_HAVE_EXPERIENCE_WITH_title, {
                                                  title: article.instrumentalTitle,
                                              })}
                                    </div>
                                    <div
                                        className={
                                            styles.ArticleExperiences__addExperience__message
                                        }
                                    >
                                        {WIKI_SHARE_YOUR_EXPERIENCE_AND_HELP_OTHERS}
                                    </div>
                                    <TrackClick
                                        name="wiki_experience_add_clicked"
                                        props={{
                                            article_id: article.id,
                                            category_slug: article.category.slug,
                                            article_slug: article.slug,
                                        }}
                                    >
                                        <Btn
                                            className={
                                                styles.ArticleExperiences__addExperience__button
                                            }
                                            type={BtnType.Blue}
                                            icon={faComment}
                                            label={WIKI_WRITE_YOUR_EXPERIENCE}
                                            link={addExpUrl}
                                        />
                                    </TrackClick>
                                </div>

                                {experiences.length > 1 && (
                                    <div className={styles.ArticleExperiences__sorting}>
                                        <SimpleSelect
                                            type="bigger"
                                            selected={this.state.sortExperiencesBy}
                                            options={sortOptions}
                                            onChange={this.onSortExperiencesByChange}
                                        />
                                    </div>
                                )}

                                {experiences.map((experience, id) => {
                                    return (
                                        <Experience
                                            key={id}
                                            experience={experience}
                                            isAdmin={isAdmin}
                                            isMobile={isMobile}
                                            onDeleteVendorReply={onDeleteVendorReply}
                                            onSaveVendorReply={onSaveVendorReply}
                                            onToggleMenuMore={onToggleMenuMore}
                                            showCompact={experience.article.id !== article.id}
                                            location={location}
                                        />
                                    );
                                })}
                            </div>
                        )}

                    {subCategoriesOfCurrentCategory}
                    {reviewArticlesOfCurrentCategory}
                    <ReviewHomepageRecentBlogArticles
                        articleSlug={articleSlug}
                        key={"recent-blog-articles"}
                    />
                </div>

                {article.category.slug !== strollersPrefix && relatedArticles.length > 0 && (
                    <>
                        <h2 className={styles.ArticleExperiences__sectionHeader}>
                            {WIKI_WHERE_NEXT}
                        </h2>
                        <WikiArticles
                            className={styles.Article__relatedArticles}
                            articlesData={relatedArticles}
                            isMobile={isMobile}
                            location={location}
                        />
                    </>
                )}
            </>
        );
    };

    private handleOnLoadMoreReviewCategoryArticles = () => {
        const {
            articleSlug,
            categorySlug,
            onLoadMoreReviewCategoryArticles,
            reviewCategoryArticles,
        } = this.props;
        if (reviewCategoryArticles) {
            onLoadMoreReviewCategoryArticles(
                articleSlug,
                categorySlug,
                reviewCategoryArticles.length,
            );
        }
    };

    private onClickSetState = (state) => {
        this.setState(state);
    };

    private onSortExperiencesByChange = (value) => {
        this.setState({
            sortExperiencesBy: value,
        });
    };

    private shouldShowSection = (section: Section, activeSection: Section): boolean => {
        if (activeSection === "default") {
            return true;
        }
        return activeSection === section;
    };
}

export default ReviewArticle;
